// src/pages/ProductsPage.js
import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Search, X, ChevronDown } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { SkeletonCard } from '../components/Loader';
import { useProducts, CATEGORIES } from '../hooks/useProducts';

const SORT_OPTIONS = [
  { value: 'featured',   label: 'Featured'             },
  { value: 'price-low',  label: 'Price: Low to High'   },
  { value: 'price-high', label: 'Price: High to Low'   },
  { value: 'rating',     label: 'Top Rated'            },
  { value: 'newest',     label: 'Newest First'         },
];

export default function ProductsPage() {
  const { products, loading }        = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy,       setSortBy]    = useState('featured');
  const [filterOpen,   setFilterOpen] = useState(false);
  const [priceRange,   setPriceRange] = useState([0, 10000]);
  const [searchInput,  setSearchInput] = useState(searchParams.get('search') || '');

  const activeCategory = searchParams.get('category') || 'all';
  const activeSearch   = searchParams.get('search')   || '';
  const activeFilter   = searchParams.get('filter')   || '';

  const setCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat === 'all') params.delete('category');
    else params.set('category', cat);
    params.delete('search');
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput.trim()) params.set('search', searchInput.trim());
    else params.delete('search');
    setSearchParams(params);
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (activeCategory && activeCategory !== 'all') {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (activeSearch) {
      const q = activeSearch.toLowerCase();
      list = list.filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    if (activeFilter === 'new') list = list.filter((p) => p.isNew);

    list = list.filter((p) => (p.price || 0) >= priceRange[0] && (p.price || 0) <= priceRange[1]);

    switch (sortBy) {
      case 'price-low':  list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case 'price-high': list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      case 'rating':     list.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      case 'newest':     list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)); break;
      default:           list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }
    return list;
  }, [products, activeCategory, activeSearch, activeFilter, priceRange, sortBy]);

  const pageTitle = activeSearch
    ? `Results for "${activeSearch}"`
    : activeCategory !== 'all'
      ? CATEGORIES.find((c) => c.id === activeCategory)?.label || 'Shop'
      : 'All Gifts';

  return (
    <div className="min-h-screen bg-cream pt-20">
      {/* Page Header */}
      <div className="bg-white border-b border-rose-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-bold text-3xl text-gray-900 mb-1">{pageTitle}</h1>
          <p className="text-gray-400 font-body text-sm">
            {loading ? 'Loading...' : `${filteredProducts.length} products found`}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="text" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search gifts…"
                className="w-full pl-10 pr-4 py-3 bg-white border border-rose-100 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-gray-700" />
              {searchInput && (
                <button type="button"
                  onClick={() => { setSearchInput(''); const p = new URLSearchParams(searchParams); p.delete('search'); setSearchParams(p); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>
            <button type="submit"
              className="bg-rose-500 text-white px-5 py-3 rounded-2xl hover:bg-rose-600 transition-colors text-sm font-medium">
              Search
            </button>
          </form>
          <div className="flex gap-3">
            <button onClick={() => setFilterOpen(!filterOpen)}
              className="flex items-center gap-2 px-4 py-3 bg-white border border-rose-100 rounded-2xl hover:border-rose-400 transition-all text-sm font-body font-medium text-gray-700">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <div className="relative">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-4 pr-10 py-3 bg-white border border-rose-100 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm text-gray-700 cursor-pointer">
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            </div>
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="bg-white rounded-2xl border border-rose-100 p-5 mb-6">
            <h3 className="font-body font-semibold text-gray-900 mb-3">Price Range</h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500 font-body">₹{priceRange[0]}</span>
              <input type="range" min="0" max="10000" step="100" value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                className="flex-1 accent-rose-500" />
              <span className="text-sm text-gray-500 font-body">₹{priceRange[1]}</span>
            </div>
          </div>
        )}

        {/* Category pills */}
        <div className="flex gap-2 flex-wrap mb-8">
          {CATEGORIES.map((cat) => (
            <button key={cat.id} onClick={() => setCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeCategory === cat.id
                  ? 'bg-rose-500 text-white shadow-rose'
                  : 'bg-white border border-rose-100 text-gray-600 hover:border-rose-400 hover:text-rose-500'
              }`}>
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="font-display font-semibold text-2xl text-gray-700 mb-2">No gifts found</h3>
            <p className="text-gray-400 font-body mb-6">Try adjusting your search or filters</p>
            <button onClick={() => { setSearchParams({}); setSearchInput(''); }}
              className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-medium hover:bg-rose-600 transition-colors">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}